import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import { COMPONENT_STYLES } from "utils/designTokens";

function MiniStatCard({
  bgColor = "white",
  title = { fontWeight: "medium", text: "" },
  count,
  percentage = { color: "success", text: "" },
  icon,
  direction = "right",
}) {
  return (
    <Card sx={{ padding: "17px", height: "75px", overflow: "hidden" }}>
      <VuiBox height="100%" display="flex" alignItems="center">
          <Grid container alignItems="center" sx={{ width: "100%" }}>
            {direction === "left" ? (
              <Grid item sx={{ flexShrink: 0 }}>
                <VuiBox
                  sx={{ ...COMPONENT_STYLES.glassIconBox, overflow: "hidden" }}
                  width="2.5rem"
                  height="2.5rem"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  {icon.component}
                </VuiBox>
              </Grid>
            ) : null}
            <Grid item xs={8}>
              <VuiBox ml={direction === "left" ? 2 : 0} lineHeight={1}>
                <Tooltip title={title.text} placement="top" arrow>
                  <VuiTypography
                    variant="button"
                    color={bgColor === "white" ? "text" : "white"}
                    opacity={bgColor === "white" ? 1 : 0.7}
                    textTransform="capitalize"
                    fontWeight={title.fontWeight}
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                      cursor: "default",
                    }}
                  >
                    {title.text}
                  </VuiTypography>
                </Tooltip>
                <Tooltip title={`${count} ${percentage.text || ""}`} placement="top" arrow>
                  <VuiTypography 
                    variant="subtitle2" 
                    fontWeight="medium" 
                    color="white"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {count}{" "}
                    <VuiTypography variant="button" color={percentage.color} fontWeight="bold">
                      {percentage.text}
                    </VuiTypography>
                  </VuiTypography>
                </Tooltip>
              </VuiBox>
            </Grid>
            {direction === "right" ? (
              <Grid item xs={4} sx={{ flexShrink: 0 }}>
                <VuiBox
                  sx={{ ...COMPONENT_STYLES.glassIconBox, overflow: "hidden" }}
                  width="2.5rem"
                  height="2.5rem"
                  marginLeft="auto"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  {icon.component}
                </VuiBox>
              </Grid>
            ) : null}
          </Grid>
      </VuiBox>
    </Card>
  );
}

MiniStatCard.propTypes = {
  bgColor: PropTypes.oneOf([
    "white", "primary", "secondary", "info", "success", "warning", "error", "dark",
  ]),
  title: PropTypes.shape({
    fontWeight: PropTypes.oneOf(["light", "regular", "medium", "bold"]),
    text: PropTypes.string,
  }),
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary", "secondary", "info", "success", "warning", "error", "dark", "white",
    ]),
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  icon: PropTypes.shape({
    component: PropTypes.node.isRequired,
  }).isRequired,
  direction: PropTypes.oneOf(["right", "left"]),
};

export function MiniStatCardSkeleton() {
  return (
    <Card sx={{ padding: "17px", height: "75px", overflow: "hidden" }}>
      <VuiBox height="100%" display="flex" alignItems="center">
        <Grid container alignItems="center" sx={{ width: "100%" }}>
          <Grid item xs={8}>
            <VuiBox lineHeight={1}>
              <Skeleton animation="wave" width="55%" height={18} sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
              <Skeleton animation="wave" width="35%" height={22} sx={{ mt: 0.5, bgcolor: "rgba(255,255,255,0.08)" }} />
            </VuiBox>
          </Grid>
          <Grid item xs={4}>
            <Skeleton
              animation="wave"
              variant="circular"
              width="3rem"
              height="3rem"
              sx={{ ml: "auto", bgcolor: "rgba(255,255,255,0.08)" }}
            />
          </Grid>
        </Grid>
      </VuiBox>
    </Card>
  );
}

export default MiniStatCard;
